<script setup lang="ts">
import { computed, onMounted, ref, toRefs } from 'vue';
import useVuelidate from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import Textarea from 'primevue/textarea';
import { AxiosResponse } from 'axios';
import ConfirmAction from '@/components/ConfirmAction';
import useUserTreatmentPlanStore from '@/store/People/Person/TreatmentPlans';
import { TreatmentGoalFormInterface } from '@/types/TreatmentPlans';
import { PersonStores } from '@/store';
import { can } from '@/composables';
import { EMIT, PERMISSIONS } from '@/enum';

interface Props {
  goal: TreatmentGoalFormInterface;
  serviceId: number;
}

const treatmentPlanStore = useUserTreatmentPlanStore();
const treatmentPlansStore = PersonStores.useUserTreatmentPlansStore();
const toggleConfirmModal = ref(false);
const emit = defineEmits([EMIT.REMOVE, EMIT.ERROR, EMIT.SUCCESS, EMIT.NEW_GOAL, EMIT.UPDATE]);
const props = defineProps<Props>();
const { goal, serviceId } = toRefs(props);
const editedGoal = ref<TreatmentGoalFormInterface>({ ...goal.value, treatment_goal_id: goal.value.id });
const complete = ref(!!goal.value.completed_at);
const currentDate = new Date();
const canEdit = computed(() => can([PERMISSIONS.TREATMENT_GOALS_UPDATE, PERMISSIONS.TREATMENT_SERVICES_GOALS_STORE]));
const savingGoal = ref(false);

const rules = computed(() => ({
  description: {
    required,
  },
  treatment_goal_type_id: {
    required,
  },
}));

const v$ = useVuelidate(rules, editedGoal);

onMounted(async () => {
  if (can([PERMISSIONS.TREATMENT_GOAL_TYPES_INDEX])) await treatmentPlansStore.getTreatmentPlanGoalsTypes();
});

const toggleSwitch = async () => {
  if (complete.value)
    editedGoal.value.completed_at =
      currentDate.toLocaleDateString('fr-CA') + ' ' + currentDate.toTimeString().split(' ')[0];
  else editedGoal.value.completed_at = '';

  treatmentPlanStore
    .editGoal({ treatment_goals: [editedGoal.value] }, serviceId.value as number)
    .then(onCompleteSuccess)
    .catch(onCompleteFailed);
};

const onCompleteSuccess = () => {
  if (complete.value) emit(EMIT.SUCCESS, 'Goal has been marked complete');
  else emit(EMIT.SUCCESS, 'Goal has been marked incomplete');
};

const onCompleteFailed = (error: AxiosResponse) => {
  emit(EMIT.ERROR, error.data.message);
};

const hasId = computed(() => !!goal.value.id);

const canComplete = computed<boolean>(() => hasId.value && canEdit.value);

const openConfirmModal = () => (toggleConfirmModal.value = true);

const removeGoal = () => {
  toggleConfirmModal.value = false;
  emit(EMIT.REMOVE, editedGoal.value);
};

const onCancel = () => {
  editedGoal.value = { ...goal.value, treatment_goal_id: goal.value.id };
  v$.value.$reset();
};

const onSave = async () => {
  savingGoal.value = true;
  if (editedGoal.value.id) {
    if (can([PERMISSIONS.TREATMENT_SERVICES_GOALS_UPDATE])) {
      await treatmentPlansStore.editGoal({ treatment_goals: [editedGoal.value] }, serviceId.value as number);
      emit(EMIT.UPDATE, 'Goal has been Updated');
    }
  } else {
    if (can([PERMISSIONS.TREATMENT_SERVICES_GOALS_UPDATE])) {
      await treatmentPlansStore
        .addGoal({ treatment_goals: [editedGoal.value] }, serviceId.value as number)
        .catch((err) => {
          return err;
        });
      emit(EMIT.NEW_GOAL, editedGoal.value);
      v$.value.$reset();
    }
  }
  savingGoal.value = false;
};
</script>

<template>
  <div
    class="p-2 mt-3 ml-3 surface-100 border-4 border-round-xl"
    :class="v$.$dirty ? 'border-color-green-light' : 'border-100'"
  >
    <div class="flex">
      <Dropdown
        id="treatment_goals_type_id"
        v-model="v$.treatment_goal_type_id.$model"
        :options="treatmentPlansStore.treatmentPlanGoalsTypes"
        option-label="title"
        option-value="id"
        placeholder="Goal Type"
        class="mr-2"
        style="min-width: 150px"
        :disabled="!canEdit"
        @change="v$?.$touch()"
      />
      <Textarea
        id="description"
        v-model="v$.description.$model"
        type="text"
        placeholder="Description"
        class="w-full"
        :auto-resize="true"
        rows="1"
        cols="30"
        :disabled="!canEdit"
        :class="{ 'p-invalid': v$?.description.$errors.length }"
        @keyup="v$?.$touch()"
      />
      <span
        v-if="can([PERMISSIONS.TREATMENT_SERVICES_GOALS_DELETE])"
        v-tooltip.top="'Remove goal'"
        class="ml-3 my-1 m-icon delete text-color-secondary cursor-pointer"
        role="button"
        @click.prevent="openConfirmModal"
      ></span>
    </div>
    <div class="flex justify-content-between mt-3 w-full">
      <div v-if="canComplete" class="flex my-1">
        <div class="flex align-items-center">
          <p class="mr-2">Mark Complete</p>
          <InputSwitch v-model="complete" @change="toggleSwitch" />
        </div>
        <div class="flex align-items-center ml-3">
          <div class="progress-indicator bg-white border-round-sm">
            <span
              class="progress-indicator-percentage"
              :style="{ width: editedGoal.measurements_average_percentage + '%' }"
            ></span>
          </div>
          <p class="ml-2 text-color-secondary">
            <b>{{ editedGoal.measurements_average_percentage }}%</b>
            Achieved
            <i v-if="editedGoal.measurements_count > 0">out of {{ editedGoal.measurements_count }} attempts</i>
          </p>
        </div>
      </div>
      <div v-if="v$?.$dirty" class="flex ml-auto">
        <AppButton type="secondary" text="Cancel" class="ml-3" @click="onCancel" />
        <AppButton
          v-if="can([PERMISSIONS.TREATMENT_SERVICES_GOALS_UPDATE, PERMISSIONS.TREATMENT_SERVICES_GOALS_STORE])"
          type="primary"
          text="Save Goal"
          class="ml-3"
          :loading="savingGoal"
          :disabled="v$?.$errors.length > 0"
          @click="onSave"
        />
      </div>
    </div>
  </div>
  <ConfirmAction
    v-if="toggleConfirmModal && can([PERMISSIONS.TREATMENT_SERVICES_GOALS_DELETE])"
    header="Delete goal"
    accept-label="Delete"
    @accept="removeGoal"
    @cancel="toggleConfirmModal = false"
  >
    <span>Are you sure that you want to remove this goal ?</span>
  </ConfirmAction>
</template>

<style lang="scss">
.progress-indicator {
  width: 120px;
  height: 1rem;
  position: relative;
  overflow: hidden;
  .progress-indicator-percentage {
    position: absolute;
    left: 0px;
    top: 0px;
    height: 100%;
    background: var(--primary-color-medium);
    min-width: 0.25rem;
    width: 0.25rem;
    transition: 0.2s ease;
  }
}
</style>
